import { createElement } from 'react'
import { Head, usePage } from '@inertiajs/react'
import type { SharedProps } from '../types'

// Server-managed <head> tags on client-side visits.
//
// The first page load gets its <title>, description, canonical, OpenGraph and
// JSON-LD straight from the HTML (inertia_meta_tags in the Rails layout). An
// Inertia visit only fetches JSON, so without this the head would keep the
// previous page's tags. The controller sends the same tags as the
// `_inertia_meta` prop; this re-renders them through <Head>, keyed by the
// server's head-key so they replace rather than pile up.
//
// Adapted from the inertia-rails cookbook ("Server Managed Meta Tags", the
// component for Inertia clients older than 3.5 — this app is on 2.x). Render
// it once, in AppLayout. Pages under TodoLayout / WikiLayout / AdminLayout are
// behind login and set their titles with <Head title> themselves.
// Inner content goes through Inertia's head manager as an HTML string, so it
// is escaped here the way the server escapes it (tag helper for text,
// ERB::Util.json_escape for JSON-LD). Without it a title containing
// "</title>" — artifact titles are anyone's — is cut short after hydration,
// and the client copy no longer matches the server's.
const escapeText = (text: string) => text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
const escapeJson = (json: string) =>
  json.replace(/</g, '\\u003c').replace(/>/g, '\\u003e').replace(/&/g, '\\u0026')

export default function MetaTags() {
  const meta = usePage<SharedProps>().props._inertia_meta ?? []

  return (
    <Head>
      {meta.map(({ tagName, innerContent, headKey, httpEquiv, ...attrs }) => {
        const html =
          innerContent == null
            ? undefined
            : typeof innerContent === 'string'
              ? escapeText(innerContent)
              : escapeJson(JSON.stringify(innerContent))

        return createElement(tagName, {
          key: headKey,
          'head-key': headKey,
          ...(httpEquiv ? { 'http-equiv': httpEquiv } : {}),
          ...attrs,
          ...(html ? { dangerouslySetInnerHTML: { __html: html } } : {}),
        })
      })}
    </Head>
  )
}
